import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import NavBar from "../NavBarPage";
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

const FinanceConfigPage = () => {
    const { apiFetch, user, logout } = useAuth();
    const [sessionExpired, setSessionExpired] = useState(false);
    const navigate = useNavigate();
    const [mappings, setMappings] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [codeSearchTerm, setCodeSearchTerm] = useState('');
    const [savingStates, setSavingStates] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [conflictInfo, setConflictInfo] = useState(null);
    const [inputValues, setInputValues] = useState({});

    const fetchMappings = async () => {
        try {
            const result = await apiFetch('/api/Finance/mda-account-mappings');
            const data = await result.json();
            if (data.success) {        
                setMappings(data.data);
                setInputValues({});
            } else {
                throw new Error('Failed to fetch mappings');
            }   
        } catch (e)
        {
            Sentry.captureException(e);
            if (e.message && e.message.includes('Session expired')) {
                setSessionExpired(true);
                logout();
                return;
            }
            console.error(e)
        } 
    }

    const filteredMappings = mappings.filter(mapping => {
        const matchesAccountName = mapping.mdaAccountName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesTransactionCode = mapping.transactionCode?.toString().toLowerCase().includes(codeSearchTerm.toLowerCase());

        if (searchTerm && codeSearchTerm) {
            return matchesAccountName && matchesTransactionCode;
        }
        return (searchTerm ? matchesAccountName : true) &&
            (codeSearchTerm ? matchesTransactionCode : true);
    });

    useEffect(() => {
        fetchMappings();
    }, []);

    const handleTransactionCodeChange = async (mdaAccountName, value) => {
        // Only validate and save if there's a value or it was previously set
        if (value || mappings.find(m => m.mdaAccountName === mdaAccountName)?.transactionCode) {
            // Validate the transaction code is a number between 1-3600
            if (value && !(/^\d+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 3600)) {
                toast.error('Transaction code must be a number between 1 and 3600');
                return;
            }

            // Set saving state for this specific field
            setSavingStates(prev => ({ ...prev, [mdaAccountName]: true }));

            // User ID used in API call (logging removed to prevent cleartext logging)

            try {
                const result = await apiFetch('/api/Finance/update-transaction-code', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        mdaAccountName,
                        transactionCode: value,
                        UserID: user.userID
                    })
                });

                const data = await result.json();

                if (data.success) {
                    // Update local state
                    setMappings(prev =>
                        prev.map(m =>
                            m.mdaAccountName === mdaAccountName
                                ? { ...m, transactionCode: value }
                                : m
                        )
                    );
                    await fetchMappings();
                    toast.success('Transaction code updated');
                } else {
                    // If transaction code is already in use
                    if (data.data?.existingAccountName) {
                        setConflictInfo({
                            mdaAccountName,
                            transactionCode: value,
                            existingAccountName: data.data.existingAccountName,
                        });
                        setShowModal(true);
                    } else {
                        toast.error(data.message || 'Failed to update transaction code');
                    }
                }
            } catch (error) {
                Sentry.captureException(error);
                if (error.message && error.message.includes('Session expired')) {
                    setSessionExpired(true);
                    logout();
                    return;
                }
                console.error(error);
                toast.error('An error occurred while updating the transaction code');
            } finally {
                setSavingStates(prev => ({ ...prev, [mdaAccountName]: false }));
            }
        }
    };

    const handleConfirmOverwrite = async () => {
        if (conflictInfo) {
            try {
                // First, clear the existing mapping
                await apiFetch('/api/Finance/update-transaction-code', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        mdaAccountName: conflictInfo.existingAccountName,
                        transactionCode: null,
                        UserID: user.userID
                    })
                });

                // Then set the new mapping
                const result = await apiFetch('/api/Finance/update-transaction-code', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        mdaAccountName: conflictInfo.mdaAccountName,
                        transactionCode: conflictInfo.transactionCode,
                        UserID: user.userID
                    })
                });

                const data = await result.json();

                if (data.success) {
                    // Update both mappings in local state
                    setMappings(prev =>
                        prev.map(m => {
                            if (m.mdaAccountName === conflictInfo.mdaAccountName) {
                                return { ...m, transactionCode: conflictInfo.transactionCode };
                            } else if (m.mdaAccountName === conflictInfo.existingAccountName) {
                                return { ...m, transactionCode: null };
                            }
                            return m;
                        })
                    );
                    await fetchMappings();
                    toast.success('Transaction code reassigned successfully');
                } else {
                    toast.error(data.message || 'Failed to reassign transaction code');
                }
            } catch (error) {
                Sentry.captureException(error);
                if (error.message && error.message.includes('Session expired')) {
                    setSessionExpired(true);
                    logout();
                    return;
                }
                console.error(error);
                toast.error('An error occurred while reassigning the transaction code');
            } finally {
                setShowModal(false);
                setConflictInfo(null);
            }
        }
    };

    const handleInputBlur = (mdaAccountName, value) => {
        handleTransactionCodeChange(mdaAccountName, value);
    };

    const handleKeyPress = (e, mdaAccountName, value) => {
        if (e.key === 'Enter') {
            handleTransactionCodeChange(mdaAccountName, value);
            e.target.blur(); // Remove focus from the input
        }
    };

    const handleCancelOverwrite = () => {
        setShowModal(false);

        if (conflictInfo) {
            // Find the current mapping for this MDA account
            const originalMapping = mappings.find(m => m.mdaAccountName === conflictInfo.mdaAccountName);

            // Reset both states for this specific item
            setMappings(prev =>
                prev.map(m =>
                    m.mdaAccountName === conflictInfo.mdaAccountName
                        ? { ...m, transactionCode: originalMapping.transactionCode || '' } // Revert to original value
                        : m
                )
            );

            // Reset the input value state as well
            setInputValues(prev => ({
                ...prev,
                [conflictInfo.mdaAccountName]: originalMapping.transactionCode || '' // Revert to original value
            }));
        }

        setConflictInfo(null);
    };

    if (sessionExpired) {
        return (
            <div className="error-message">
                <p>Your session has expired. Please log in again.</p>
                <button onClick={() => navigate('/')}>Go to Login</button>
            </div>
        );
    }

    return (
        <div>
            <div className="finance-config-container">
                <h1>MDA Account Transaction Code Mapping</h1>
                <p style={{ color: '#555', fontSize: '0.92rem', marginBottom: '16px', lineHeight: 1.5 }}>
                    Map each MDA (Management Data Accounting) account to a numeric transaction code used by the financial system.
                    Transaction codes must be between 1 and 3600. If a code is already assigned to another account, you will be prompted to reassign it.
                    Changes are saved automatically when you leave the field or press Enter.
                </p>

                <div className="search-container">
                    <div className="search-row">
                        <div className="search-field">
                            <label htmlFor="accountSearch">Search by MDA Account:</label>
                            <input
                                id="accountSearch"
                                type="text"
                                placeholder="Search by account name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                        <div className="search-field">
                            <label htmlFor="codeSearch">Search by Transaction Code:</label>
                            <input
                                id="codeSearch"
                                type="text"
                                placeholder="Search by code..."
                                value={codeSearchTerm}
                                onChange={(e) => setCodeSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                    </div>
                    <div className="search-results-info">
                        Found {filteredMappings.length} out of {mappings.length} mappings
                    </div>
                </div>

                <div className="mapping-list">
                    <table className="mapping-table">
                        <thead>
                            <tr>
                                <th>MDA Account Name</th>
                                <th style={{ textAlign: "left" }}>Transaction Code</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredMappings.length > 0 ? (
                                filteredMappings.map((mapping, index) => (
                                    <tr key={index} className={
                                        codeSearchTerm && mapping.transactionCode?.toString().toLowerCase().includes(codeSearchTerm.toLowerCase())
                                            ? "highlighted-row"
                                            : ""
                                    }>
                                        <td>{mapping.mdaAccountName}</td>
                                        <td>
                                            <input
                                                type="text"
                                                value={inputValues[mapping.mdaAccountName] !== undefined
                                                    ? inputValues[mapping.mdaAccountName]
                                                    : mapping.transactionCode || ''}
                                                onChange={(e) => {
                                                    const newValue = e.target.value;
                                                    setInputValues(prev => ({
                                                        ...prev,
                                                        [mapping.mdaAccountName]: newValue
                                                    }));
                                                }}
                                                placeholder="Enter code (1-3600)"
                                                className="transaction-code-input"
                                                maxLength={4}
                                                onBlur={(e) => handleInputBlur(mapping.mdaAccountName, e.target.value)}
                                                onKeyPress={(e) => handleKeyPress(e, mapping.mdaAccountName, e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            {savingStates[mapping.mdaAccountName] ? (
                                                <span className="saving-indicator">Saving...</span>
                                            ) : (inputValues[mapping.mdaAccountName] !== undefined) ? (
                                                <span className="unsaved-indicator">Unsaved</span>
                                            ) : mapping.transactionCode ? (
                                                <span className="saved-indicator">Saved</span>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="no-data">
                                        {(searchTerm || codeSearchTerm)
                                            ? 'No matching MDA accounts found'
                                            : 'No MDA accounts available'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Transaction Code Conflict Modal */}
                {showModal && conflictInfo && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <h2>Transaction Code Conflict</h2>
                            <p>
                                Transaction code <strong>{conflictInfo.transactionCode}</strong> is already
                                assigned to <strong>{conflictInfo.existingAccountName}</strong>.
                            </p>
                            <p>Do you want to reassign this code to <strong>{conflictInfo.mdaAccountName}</strong>?</p>
                            <div className="modal-actions">
                                <button
                                    className="confirm-button"
                                    onClick={handleConfirmOverwrite}
                                >
                                    Yes, Reassign
                                </button>
                                <button
                                    className="cancel-button"
                                    onClick={handleCancelOverwrite}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    )
}

export default FinanceConfigPage;